// Value formatting / normalization — the contract from the design reference's
// `parser.js` ("These rules are implemented in parser.js and must be preserved",
// handoff "Value Formatting"). Storage format vs. display format are separate:
// `normalize` produces what we store and send, `displayValue` produces what the
// student reads in a receipt or a collapsed row.

import { CfField, CfGroupInstance, FieldValue } from './types';

const MONTHS: Record<string, number> = {
    jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
    may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9,
    september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

export const fmtSSN = (raw: unknown): string => {
    const d = String(raw ?? '').replace(/\D/g, '').slice(0, 9);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

export const fmtPhone = (raw: unknown): string => {
    const digits = String(raw ?? '').replace(/\D/g, '');
    // "+1 512 555 0142" is an ordinary way to state a US number; without dropping
    // the country code the mask would shift every digit one place left.
    const d = (digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** Sensitive display: last four only. */
export const maskSSN = (formatted: unknown): string => {
    const d = String(formatted ?? '').replace(/\D/g, '');
    if (!d) return '';
    return `•••-••-${d.slice(-4).padStart(4, '•')}`;
};

export const fmtZip = (raw: unknown): string => String(raw ?? '').replace(/\D/g, '').slice(0, 5);

/**
 * MM/DD/YYYY. Accepts `5/4/2003`, `2003-05-04`, `May 4, 2003`. Returns the input
 * trimmed when nothing parses — never throws, never invents a date.
 */
export const fmtDate = (raw: unknown): string => {
    const text = String(raw ?? '').trim();
    if (!text) return '';

    const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return `${iso[2].padStart(2, '0')}/${iso[3].padStart(2, '0')}/${iso[1]}`;

    const numeric = text.match(/\b(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/);
    if (numeric) {
        const year = numeric[3].length === 2 ? `19${numeric[3]}` : numeric[3];
        return `${numeric[1].padStart(2, '0')}/${numeric[2].padStart(2, '0')}/${year}`;
    }

    const named = text.match(/\b([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})\b/i);
    if (named && MONTHS[named[1].toLowerCase()]) {
        const month = String(MONTHS[named[1].toLowerCase()]).padStart(2, '0');
        return `${month}/${named[2].padStart(2, '0')}/${named[3]}`;
    }

    return text;
};

/** MM/YYYY. Accepts `5/2003`, `May 2003`, `2003-05`. */
export const fmtMonthYear = (raw: unknown): string => {
    const text = String(raw ?? '').trim();
    if (!text) return '';

    const iso = text.match(/^(\d{4})-(\d{1,2})$/);
    if (iso) return `${iso[2].padStart(2, '0')}/${iso[1]}`;

    const numeric = text.match(/^(\d{1,2})[/\-.](\d{4})$/);
    if (numeric) return `${numeric[1].padStart(2, '0')}/${numeric[2]}`;

    const named = text.match(/\b([a-z]+)\s+(\d{4})\b/i);
    if (named && MONTHS[named[1].toLowerCase()]) {
        return `${String(MONTHS[named[1].toLowerCase()]).padStart(2, '0')}/${named[2]}`;
    }

    return text;
};

/** Widen a value array so its members are filterable — TS cannot call
 *  `.filter` on the `string[] | CfGroupInstance[]` union directly. */
const members = (value: FieldValue): Array<string | CfGroupInstance> =>
    Array.isArray(value) ? (value as Array<string | CfGroupInstance>) : [];

const asArray = (value: FieldValue): string[] =>
    Array.isArray(value)
        ? members(value).filter((v): v is string => typeof v === 'string')
        : [value];

/** A `group` value: instances only — anything else reads as none. */
export const asInstances = (value: FieldValue | undefined | null): CfGroupInstance[] =>
    value == null
        ? []
        : members(value).filter(
              (v): v is CfGroupInstance => typeof v === 'object' && v !== null && !Array.isArray(v)
          );

const labelFor = (field: CfField, value: string): string => {
    const hit = field.options?.find((o) => o.value === value);
    return hit ? hit.label : value;
};

/**
 * Human-readable rendering of a stored value: radio shows its option label,
 * checklist comma-joins labels, daterange spans an en dash, sensitive values
 * are masked. Used by receipts and (later) collapsed confirmed rows.
 */
export const displayValue = (field: CfField, value: FieldValue | undefined | null): string => {
    if (value == null || value === '') return '';
    if (Array.isArray(value) && value.length === 0) return '';

    switch (field.type) {
        case 'daterange':
            return String(value).replace('|', ' – ');
        case 'ssn':
            return field.sensitive ? maskSSN(value) : String(value);
        case 'radio':
        case 'select':
            return labelFor(field, String(value));
        // Consent shows its checked attestation labels, like a checklist.
        case 'checklist':
        case 'consent':
            return asArray(value).map((v) => labelFor(field, v)).join(', ');
        case 'group': {
            // Instance count, per the design reference's parser ("1 entry" /
            // "N entries") — the instances themselves render in the control.
            const count = asInstances(value).length;
            return `${count} ${count === 1 ? 'entry' : 'entries'}`;
        }
        default:
            return String(value);
    }
};

/** Normalize an extracted or typed value into the field's storage format. */
export const normalize = (field: CfField, raw: unknown): FieldValue => {
    switch (field.type) {
        case 'ssn':
            return fmtSSN(raw);
        case 'phone':
            return fmtPhone(raw);
        case 'zip':
            return fmtZip(raw);
        case 'date':
            return fmtDate(raw);
        case 'monthyear':
            return fmtMonthYear(raw);
        case 'checklist': {
            const values = Array.isArray(raw) ? raw : [raw];
            return values
                .map((v) => String(v ?? '').trim())
                .filter(Boolean)
                .map((v) => matchOption(field, v));
        }
        case 'radio':
        case 'select':
            return matchOption(field, String(raw ?? '').trim());
        // Consent stores the checked attestation ids, checklist-style.
        case 'consent': {
            const values = Array.isArray(raw) ? raw : [raw];
            return values
                .map((v) => String(v ?? '').trim())
                .filter(Boolean)
                .map((v) => matchOption(field, v));
        }
        // Group stores instance objects as-is — stringifying them would
        // destroy the value. Anything that is not an instance array is none.
        case 'group':
            return asInstances(raw as FieldValue);
        default:
            return String(raw ?? '').trim();
    }
};

/**
 * Option matching is case-insensitive and accepts either the option value or its
 * label — extraction is contractually supposed to return values, but a label
 * slipping through should still land on the right option rather than be stored raw.
 */
const matchOption = (field: CfField, raw: string): string => {
    if (!field.options?.length) return raw;
    const lowered = raw.toLowerCase();
    const hit =
        field.options.find((o) => o.value.toLowerCase() === lowered) ??
        field.options.find((o) => o.label.toLowerCase() === lowered);
    return hit ? hit.value : raw;
};

export const isEmptyValue = (value: FieldValue | undefined | null): boolean =>
    value == null || value === '' || (Array.isArray(value) && value.length === 0);

/** A `group` instance every subfield of which is empty carries no data — the
 *  "+ Add" button's fresh `{}` is the degenerate case, but a filled-then-
 *  cleared instance reads the same way. */
const isEmptyInstance = (instance: CfGroupInstance): boolean =>
    Object.values(instance).every((v) => isEmptyValue(v as FieldValue));

/**
 * Canonical form for comparison (forms-corrections slice, D2 hardening): every
 * empty shape — `undefined`, `''`, `[]` — collapses to `undefined`, and a
 * `group` array additionally drops any instance that holds no data. Without
 * the group step, adding a blank instance and then removing it compares as
 * "changed" twice over (`undefined` → `[{}]` → `[]`), when neither step ever
 * held real data.
 */
const normalizeForCompare = (value: FieldValue | undefined): FieldValue | undefined => {
    if (Array.isArray(value) && value.length && typeof value[0] === 'object' && value[0] !== null) {
        const withData = (value as CfGroupInstance[]).filter((instance) => !isEmptyInstance(instance));
        return withData.length ? withData : undefined;
    }
    return isEmptyValue(value) ? undefined : value;
};

/**
 * Does this edit actually change anything? Forms-corrections slice (D2
 * hardening): a no-op UI gesture — re-tapping the already-selected radio,
 * re-picking the current picker value, or a group "+ Add" immediately undone
 * by "Remove" before any subfield was touched — must never flip a submitted
 * form dirty. A genuine edit still does, and this is only ever used to decide
 * whether to *set* dirty, never to clear it, so dirty stays sticky once a
 * real change has happened (design D2: reverting to the original afterward
 * does not clear it).
 */
export const sameValue = (a: FieldValue | undefined, b: FieldValue | undefined): boolean => {
    const na = normalizeForCompare(a);
    const nb = normalizeForCompare(b);
    if (na === nb) return true;
    if (na === undefined || nb === undefined) return false;
    try {
        return JSON.stringify(na) === JSON.stringify(nb);
    } catch {
        return false;
    }
};
